import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Modal,
  Row,
  Button,
  Col,
  message,
  Grid,
  Form,
  Input,
  Spin,
  Result,
  Carousel,
} from 'antd';
import {
  ArrowLeftOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  LeftCircleTwoTone,
  LinkOutlined,
  RightCircleTwoTone,
  SendOutlined,
} from '@ant-design/icons';
import API from 'utils/api';
import { onError } from 'utils/handlers';
import { getCiudadano } from 'api/cuentaunicasir';
import { sendPDFToEmail } from 'api/configuracion';
import { useSelector } from 'react-redux';
import Moment from 'moment';
import { logger } from 'utils/debug';

const {
  REACT_APP_MEDIA_BASE_URL: MEDIA_BASE_URL,
} = process.env;

const { Text } = Typography;

const getExtension = (url) => new URL(url).pathname.match(/[^\\]*\.(\w+)$/)?.[1];

export const isImageUrl = (url) => url && /jpeg|jpg|png/i.test(getExtension(url));
export const isPDFUrl = (url) => url && /pdf/i.test(getExtension(url));
export const isPDFBase64 = (b64) => /^data:application\/pdf/.test(b64);

const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

const base64WithoutType = (base64) => (base64.includes(';base64,')
  ? base64.substr(base64.indexOf(';base64,') + 8) : base64);

export const base64ToBlobUrl = (base64, type = 'application/pdf') => {
  const blob = b64toBlob(base64WithoutType(base64), type);
  const blobUrl = URL.createObjectURL(blob);
  return blobUrl;
};

export const downloadBase64 = (base64, fileName) => {
  const downloadLink = document.createElement('a');
  downloadLink.download = fileName;
  if (fileName.includes('xlsx')) {
    downloadLink.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64WithoutType(base64)}`;
  } else {
    downloadLink.href = base64;
  }
  downloadLink.click();
};

const getBaseUrl = (url) => url.split('?')[0];

const downloadFile = async (url, fileName) => {
  const updatedURL = getBaseUrl(url.replace(/^http:\/\/obsdemos\.migob\.digital\/demosigob\/media\//, MEDIA_BASE_URL));
  try {
    const a = document.createElement('a');
    a.href = updatedURL;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    navigator.clipboard.writeText(`${fileName}.pdf`);
    message.success({
      content: (
        <Col>
          El nombre del archivo ha sido copiado al portapapeles.
          <br />
          Puede pegarlo en el campo de nombre del archivo al guardar el documento.
          <br />
          <strong>
            {fileName}
          </strong>
          <Button
            onClick={() => message.destroy()}
            type="link"
            icon={<CloseCircleOutlined />}
            style={{
              position: 'absolute',
              top: -30,
              right: -20,
            }}
          />
        </Col>
      ),
      duration: 0,
    });
  } catch (err) {
    logger(err);
  }
};

const Previewer = ({
  setVisible,
  visible,
  iFrameUrl,
  isImage: _isImage,
  base64,
  allowSend,
  baseURL,
  id,
  cuentaUnicaID,
  allowSendMiCuenta,
  email,
  allowDownload,
  fileName,
  modal,
  ifameHeight,
  error,
  allowDownloadOnError,
  customFileNameFormat,
}) => {
  const theme = useSelector((s) => s.app.theme);
  const user = useSelector((s) => s.auth.user);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sendToEmail, setSendToEmail] = useState(false);
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [visibleInput, setVisibleInput] = useState(false);
  const [cuentaUnica, setCuentaUnica] = useState();
  const screens = Grid.useBreakpoint();

  const fullFileName = `${Moment().format('YYYYMMDD HH:mm:ss')} - SIR - ${fileName}.pdf`;

  const sendTo = async () => {
    try {
      setLoading(true);
      if (sendToEmail) {
        await form.validateFields();
        const values = {};
        const _fileName = await (typeof customFileNameFormat === 'function'
          ? customFileNameFormat() : fullFileName);
        values.archivo_base64 = base64.replace('data:application/pdf;base64,', '');
        values.email = email || cuentaUnica?.email;
        if (visibleInput) {
          const { email: _email } = form.getFieldsValue();
          values.email = _email;
        }
        values.nombre_del_archivo = _fileName;
        values.reporte_redaccion = fileName;
        const sent = await sendPDFToEmail(values);
        if (sent) {
          message.info('El documento ha sido enviado, revise su bandeja de recepción.');
          setVisibleConfirm(false);
          setVisibleInput(false);
        }
      } else {
        const res = await API.post(`${baseURL}${id}/enviar-micuentamx/`);
        if (res?.status === 200) {
          message.info('El documento ha sido enviado revise su bandeja de recepción');
          setVisibleConfirm(false);
          setVisibleInput(false);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    const fetchCuenta = async () => {
      if (cuentaUnicaID) {
        const _ciudadano = await getCiudadano(cuentaUnicaID);
        setCuentaUnica(_ciudadano);
      } else if (user) {
        setCuentaUnica(user);
      }
    };

    fetchCuenta();
    // eslint-disable-next-line
  }, [cuentaUnicaID]);

  const onClickSendToEmail = () => {
    setVisibleConfirm(true);
    setSendToEmail(true);
  };

  const onClickSendToMX = () => {
    setVisibleConfirm(true);
    setSendToEmail(false);
  };

  const onCancel = () => {
    if (visibleInput) {
      setVisibleInput(false);
    } else if (visibleConfirm) {
      setVisibleConfirm(false);
    } else if (setVisible) {
      setVisible(false);
    }
  };

  const SubmitControls = () => (
    <Row className="form-submit-controls">
      <Button
        type="link"
        onClick={onCancel}
      >
        <ArrowLeftOutlined />
        {screens.md && 'Regresar'}
      </Button>
      {(allowDownload && base64 && fileName && !visibleConfirm) && (
        <Button
          icon={<LinkOutlined />}
          onClick={() => {
            const blobUrl = base64ToBlobUrl(base64);
            window.open(blobUrl);
          }}
          type="link"
        >
          Abrir en otra pestaña
        </Button>
      )}
      {(allowDownload && base64 && fileName && !visibleConfirm) && (
        <Button
          icon={<DownloadOutlined />}
          onClick={async () => {
            const _fileName = await (typeof customFileNameFormat === 'function'
              ? customFileNameFormat(fileName) : fileName);
            downloadBase64(base64, _fileName);
          }}
          type="link"
        >
          Descargar
          {' '}
          {isPDFBase64(base64) ? 'PDF' : 'XLSX'}
        </Button>
      )}
      {(allowSend && !visibleConfirm) && (
        <>
          <Button
            type="link"
            onClick={onClickSendToEmail}
          >
            <SendOutlined />
            {screens.md && 'Enviar a Correo'}
          </Button>
          {allowSendMiCuenta && (
            <Button
              type="link"
              onClick={onClickSendToMX}
            >
              <SendOutlined />
              {screens.md && 'Enviar a MiCuentaMX'}
            </Button>
          )}
        </>
      )}
      {visibleConfirm && (
        <Button
          type="link"
          onClick={sendTo}
        >
          <SendOutlined />
          {screens.md && (
            sendToEmail ? 'Enviar a Correo' : 'Enviar a MiCuentaMX'
          )}
        </Button>
      )}
    </Row>
  );

  const rules = {
    email: [
      { required: true },
      { type: 'email' },
    ],
  };

  const isValidBase64 = () => {
    try {
      if (base64) {
        const _base64 = base64.substring(base64.indexOf(',') + 1);
        const decoded = window.atob(_base64);
        const encoded = window.btoa(decoded);
        const equals = encoded === _base64;
        return equals;
      }
    } catch (err) {
      logger(err);
    }
    return false;
  };

  const googleViewerURI = (
    _iFrameUrl,
  ) => `https://docs.google.com/gview?url=${encodeURIComponent(_iFrameUrl)}&embedded=true`;

  const getContent = (_iFrameUrl, style) => {
    if (isImageUrl(_iFrameUrl) || _isImage) {
      return (
        <img
          src={_iFrameUrl || base64}
          alt="img_preview"
          style={style || { maxWidth: '100%' }}
        />
      );
    }
    if (_iFrameUrl) {
      return (
        <>
          {(allowDownload && !visibleConfirm) && (
            <Row justify="end" style={{ width: '100%' }}>
              <Col>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={async () => {
                    const _fileName = await (typeof customFileNameFormat === 'function'
                      ? customFileNameFormat(fileName) : fileName);

                    downloadFile(_iFrameUrl, _fileName);
                  }}
                  type="link"
                >
                  Descargar
                  {' '}
                  {getExtension(_iFrameUrl).toUpperCase()}
                </Button>
              </Col>
            </Row>
          )}
          <iframe
            src={isPDFBase64(_iFrameUrl)
              ? base64ToBlobUrl(_iFrameUrl) : googleViewerURI(_iFrameUrl)}
            title="preview"
            style={{
              minHeight: modal ? '75vh' : null,
              height: ifameHeight,
            }}
          />
        </>
      );
    }
    if (error) {
      return (
        <Row justify="center" style={{ width: '100%' }}>
          <Col span={24}>
            <Result
              status="warning"
              title="No fue posible cargar el contenido"
              extra={error}
            />
          </Col>
          {allowDownloadOnError && (
            <Col>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadBase64}
              >
                Descargar
                {' '}
                {isPDFBase64(base64) ? 'PDF' : 'XLSX'}
              </Button>
            </Col>
          )}
        </Row>
      );
    }
    if (isValidBase64()) {
      return (
        <>
          {(allowDownload && !visibleConfirm) && (
          <Row justify="end" style={{ width: '100%' }}>
            <Col>
              <Button
                icon={<DownloadOutlined />}
                onClick={async () => {
                  const _fileName = await (typeof customFileNameFormat === 'function'
                    ? customFileNameFormat(fileName) : fileName);

                  downloadBase64(base64, _fileName);
                }}
                type="link"
              >
                Descargar
                {' '}
                {isPDFBase64(base64) ? 'PDF' : 'XLSX'}
              </Button>
            </Col>
          </Row>
          )}
          <iframe
            src={isPDFBase64(base64) ? base64ToBlobUrl(base64) : base64}
            title="preview"
            style={{
              minHeight: modal ? '75vh' : null,
              height: ifameHeight,
            }}
          />
        </>
      );
    }

    return modal ? 'No fue posible mostrar el archivo' : null;
  };

  const content = Array.isArray(iFrameUrl) ? (
    <Carousel
      arrows
      prevArrow={<LeftCircleTwoTone twoToneColor={theme.primaryText} />}
      nextArrow={<RightCircleTwoTone twoToneColor={theme.primaryText} />}
      dots={false}
    >
      {iFrameUrl.map((e) => (
        <div key={e}>
          {getContent(e, { height: '100%', width: '100%', objectFit: 'contain' })}
        </div>
      ))}
    </Carousel>
  ) : getContent(iFrameUrl);

  if (modal) {
    return (
      <>
        <Modal
          title={(
            <Row justify="space-between">
              <Text>
                Preview
              </Text>
              <SubmitControls />
            </Row>
          )}
          className={`modal-preview ${visibleConfirm ? 'padding' : ''}`}
          visible={visible}
          footer={null}
          closable={false}
          onCancel={onCancel}
          width={visibleConfirm && screens?.md ? '50%' : '90%'}
          maskClosable={false}
          centered
          destroyOnClose
          forceRender
        >
          {!visibleConfirm ? (
            <>
              {content}
            </>
          ) : (
            <Row align="center" justify="middle">
              {visibleInput ? (
                <>
                  <Spin
                    tip="Cargando..."
                    spinning={loading}
                    style={{ width: '100%', height: '100%' }}
                  >
                    <Form
                      layout="vertical"
                      form={form}
                      onFinish={sendTo}
                      scrollToFirstError
                    >
                      <Row gutter={10}>
                        <Col span={24}>
                          <Form.Item
                            name="email"
                            rules={rules.email}
                            label="Correo electrónico"
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Form.Item hidden>
                          <Button htmlType="submit" />
                        </Form.Item>
                      </Row>
                    </Form>
                  </Spin>
                </>
              ) : (
                <>
                  <Text style={{ fontSize: 16, textAlign: 'center' }}>
                    ¿Desea enviar el archivo a
                    {' '}
                    {`${cuentaUnica?.clave_ciudadana || ''} - ${cuentaUnica?.email}`.replace(' - undefined', '')}
                    ?
                    {sendToEmail && (
                    <>
                      <br />
                      o
                      <br />
                      <Button type="link" onClick={() => setVisibleInput(true)}>
                        Enviar a otro correo
                      </Button>
                    </>
                    )}
                  </Text>
                  {/* <br />
                <br />
                <Text style={{ textAlign: 'center' }}>
                  Si la información no es correcta actualicela en el módulo de ciudadanos.
                </Text> */}
                </>
              )}
            </Row>
          )}
        </Modal>
      </>
    );
  }

  if (error) {
    return (
      <Row justify="center" style={{ width: '100%' }}>
        <Result
          status="warning"
          title="No fue posible cargar el contenido"
          extra={error}
        />
      </Row>
    );
  }

  return content;
};

Previewer.propTypes = {
  setVisible: PropTypes.func,
  visible: PropTypes.bool.isRequired,
  isImage: PropTypes.bool,
  iFrameUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  base64: PropTypes.string,
  allowSend: PropTypes.bool,
  allowSendMiCuenta: PropTypes.bool,
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  baseURL: PropTypes.string,
  cuentaUnicaID: PropTypes.number,
  email: PropTypes.string,
  allowDownload: PropTypes.bool,
  fileName: PropTypes.string,
  modal: PropTypes.bool,
  ifameHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.string,
  allowDownloadOnError: PropTypes.bool,
  customFileNameFormat: PropTypes.func,
};

Previewer.defaultProps = {
  iFrameUrl: null,
  isImage: false,
  base64: null,
  allowSend: false,
  id: null,
  baseURL: null,
  cuentaUnicaID: null,
  allowSendMiCuenta: false,
  email: null,
  allowDownload: false,
  fileName: 'documento',
  modal: true,
  setVisible: null,
  ifameHeight: '100%',
  error: null,
  allowDownloadOnError: false,
  customFileNameFormat: null,
};
export default Previewer;
